import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ITEM_CATEGORIES, itemCategoryLabel } from "@/lib/constants";
import { downloadCSV } from "@/lib/export";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CalendarClock, Loader2, Trash2, CheckCircle2, Circle, Download, AlertTriangle } from "lucide-react";

const inputCls = "w-full bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none";

function dueState(item) {
  if (item.status === "completed") return { key: "completed", color: "#6D9C55", icon: CheckCircle2 };
  const now = new Date();
  const dd = item.due_date ? new Date(item.due_date) : null;
  if (dd && dd < now) return { key: "overdue", color: "#B84A22", icon: AlertTriangle };
  if (dd && dd < new Date(now.getTime() + 14 * 864e5)) return { key: "dueSoon", color: "#D98014", icon: CalendarClock };
  return { key: "pending", color: "#4B5950", icon: Circle };
}

export default function Compliance() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", regulation: "", category: "permit", due_date: "", notes: "" });

  const load = () => api.get("/compliance-items").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.title.trim() || !form.due_date) { toast.error("Title and due date are required."); return; }
    setSaving(true);
    try {
      await api.post("/compliance-items", form);
      toast.success("Obligation added.");
      setOpen(false);
      setForm({ title: "", regulation: "", category: "permit", due_date: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggle = async (item) => {
    const status = item.status === "completed" ? "pending" : "completed";
    const { data } = await api.patch(`/compliance-items/${item.id}`, { status });
    setItems((prev) => prev.map((i) => (i.id === item.id ? data : i)));
  };

  const remove = async (id) => {
    await api.delete(`/compliance-items/${id}`);
    setItems((prev) => prev.filter((i) => i.id !== id));
    toast.success("Removed.");
  };

  const exportCsv = () => {
    downloadCSV("compliance-items.csv", [
      { label: "Title", key: "title" },
      { label: "Category", get: (r) => itemCategoryLabel(r.category) },
      { label: "Regulation", key: "regulation" },
      { label: "Due Date", key: "due_date" },
      { label: "Status", get: (r) => t(`due.${dueState(r).key}`) },
      { label: "Notes", key: "notes" },
    ], items);
  };

  const sorted = [...items].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));

  return (
    <div data-testid="compliance-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><CalendarClock className="h-7 w-7 text-[#275C3F]" /> {t("comp.title")}</h1>
          <p className="text-[#4B5950] mt-1">{t("comp.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-compliance-button" onClick={exportCsv} disabled={!items.length} className="inline-flex items-center gap-2 border border-[#DCE3DD] bg-white px-3 py-2.5 rounded-sm text-sm font-medium hover:bg-[#EAF0EA] disabled:opacity-50">
            <Download className="h-4 w-4" /> {t("common.export")}
          </button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-item-button" className="inline-flex items-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white px-4 py-2.5 rounded-sm text-sm font-semibold transition-all duration-200 active:scale-[0.98]">
                <Plus className="h-4 w-4" /> {t("comp.new")}
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-display">{t("comp.addTitle")}</DialogTitle>
                <DialogDescription>{t("comp.addDesc")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">{t("fields.title")}</label>
                  <input data-testid="item-title-input" className={inputCls} value={form.title} onChange={set("title")} placeholder="e.g. Annual Tier II report" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">{t("fields.category")}</label>
                    <select data-testid="item-category-select" className={inputCls} value={form.category} onChange={set("category")}>
                      {ITEM_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{t(`category.${c.value}`)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{t("fields.dueDate")}</label>
                    <input data-testid="item-due-input" type="date" className={inputCls} value={form.due_date} onChange={set("due_date")} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("fields.regulationOpt")}</label>
                  <input data-testid="item-regulation-input" className={inputCls} value={form.regulation} onChange={set("regulation")} placeholder="e.g. 40 CFR 370" />
                </div>
                <div>
                  <label className="text-sm font-medium">{t("fields.notesOpt")}</label>
                  <textarea data-testid="item-notes-input" rows={3} className={inputCls} value={form.notes} onChange={set("notes")} />
                </div>
              </div>
              <DialogFooter>
                <button data-testid="submit-item-button" onClick={create} disabled={saving} className="inline-flex items-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("comp.addObligation")}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-dashed border-[#DCE3DD] rounded-sm py-16 text-center">
          <CalendarClock className="h-10 w-10 text-[#DCE3DD] mx-auto" />
          <p className="text-[#4B5950] mt-3">{t("comp.noItems")}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {sorted.map((item) => {
            const st = dueState(item);
            const Icon = st.icon;
            return (
              <div key={item.id} data-testid={`item-row-${item.id}`} className="bg-white border border-[#DCE3DD] rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <button data-testid={`toggle-item-${item.id}`} onClick={() => toggle(item)} className="mt-0.5 shrink-0">
                    {item.status === "completed"
                      ? <CheckCircle2 className="h-5 w-5 text-[#6D9C55]" />
                      : <Circle className="h-5 w-5 text-[#DCE3DD] hover:text-[#275C3F]" />}
                  </button>
                  <div className="min-w-0">
                    <p className={`font-medium ${item.status === "completed" ? "line-through text-[#4B5950]" : "text-[#121A14]"}`}>{item.title}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className="text-xs bg-[#EAF0EA] text-[#275C3F] px-2 py-0.5 rounded-sm">{itemCategoryLabel(item.category, t)}</span>
                      {item.regulation && <span className="text-xs text-[#2E7599]">{item.regulation}</span>}
                      {item.notes && <span className="text-xs text-[#4B5950] truncate max-w-[200px]">{item.notes}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-sm" style={{ color: st.color, background: `${st.color}1A` }}>
                    <Icon className="h-3.5 w-3.5" /> {t(`due.${st.key}`)}
                  </span>
                  <span className="text-xs text-[#4B5950] w-24 text-right">{item.due_date ? new Date(item.due_date).toLocaleDateString() : "—"}</span>
                  <button data-testid={`delete-item-${item.id}`} onClick={() => remove(item.id)} className="text-[#B84A22] hover:bg-[#B84A22]/10 p-1.5 rounded-sm"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
